import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MessageSquare, Send, Loader2 } from "lucide-react";

// Read-only MG view of HAG inspections on their weavers + comment thread per inspection.
export default function MGInspections() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "hi" ? hi : en);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState("");
  const [comments, setComments] = useState({}); // { inspection_id: [comments...] }
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/mg/inspections")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadComments = async (id) => {
    try {
      const r = await api.get(`/mg/inspections/${id}/comments`);
      setComments((c) => ({ ...c, [id]: r.data || [] }));
    } catch (e) { /* silent */ }
  };

  const openThread = (id) => {
    setOpenId(id);
    setDraft("");
    if (!comments[id]) loadComments(id);
  };

  const post = async (id) => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/mg/inspections/${id}/comment`, { comment: text });
      setDraft("");
      await loadComments(id);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const gradeChip = (g) => g ? <span className={`status-chip grade-${g}`}>{g}</span> : <span className="status-chip pace-none">—</span>;

  return (
    <div data-testid="mg-inspections">
      <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        {t("Loom Inspections", "लूम निरीक्षण")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-4">
        {t("Read-only view of HAG's field inspections on your weavers. Add comments — Supervisors & Coordinators will see them.",
           "आपके बुनकरों पर HAG के निरीक्षण — केवल पढ़ें। टिप्पणी जोड़ सकते हैं — सुपरवाइज़र व कोऑर्डिनेटर देखेंगे।")}
      </p>

      {err && <div className="mb-3 text-xs bg-red-50 border border-red-200 rounded p-2" data-testid="mg-inspections-err">{err}</div>}

      {loading ? <div className="text-sm text-[#7C8E9E]">Loading…</div> : (
        <div className="space-y-3" data-testid="mg-inspections-list">
          {rows.map((r) => (
            <div key={r.id} className="hag-surface p-3" data-testid={`mg-inspection-${r.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--brand-navy)" }}>
                    {r.milestone || t("Inspection", "निरीक्षण")} · {gradeChip(r.grade)}
                  </div>
                  <div className="text-xs text-[#4A627A] mt-0.5">
                    {t("Loom", "लूम")}: {r.loom_id?.slice(0, 8) || "—"} · {t("Date", "तिथि")}: {r.date ? new Date(r.date).toLocaleDateString() : "—"}
                  </div>
                  {r.grade_note && <div className="text-xs text-[#7C8E9E] mt-1">{r.grade_note}</div>}
                </div>
                <button data-testid={`mg-inspection-thread-${r.id}`} onClick={() => openThread(r.id)}
                  className="h-9 px-3 text-xs rounded-md border border-[#1B3A5C]/20 hover:bg-[#FAF6F0] flex items-center gap-1">
                  <MessageSquare size={14} /> {t("Comments", "टिप्पणियाँ")}
                </button>
              </div>

              {openId === r.id && (
                <div className="mt-3 border-t border-[#1B3A5C]/10 pt-3" data-testid={`mg-thread-${r.id}`}>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(comments[r.id] || []).map((c) => (
                      <div key={c.id} className="text-xs bg-[#FAF6F0] rounded-md p-2" data-testid={`mg-comment-${c.id}`}>
                        <div className="text-[10px] text-[#7C8E9E]">{new Date(c.created_at).toLocaleString()}</div>
                        <div>{c.comment}</div>
                      </div>
                    ))}
                    {(comments[r.id] || []).length === 0 && (
                      <div className="text-xs text-[#7C8E9E]">{t("No comments yet.", "अभी कोई टिप्पणी नहीं।")}</div>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input data-testid={`mg-comment-input-${r.id}`} value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && post(r.id)}
                      placeholder={t("Write a comment…", "टिप्पणी लिखें…")}
                      className="flex-1 h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
                    <button data-testid={`mg-comment-send-${r.id}`} disabled={busy || !draft.trim()}
                      onClick={() => post(r.id)}
                      className="h-10 px-3 rounded-md text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                      style={{ background: "var(--brand-navy)", color: "white" }}>
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <div className="text-sm text-[#7C8E9E]">{t("No inspections yet on your weavers.", "आपके बुनकरों पर अभी कोई निरीक्षण नहीं।")}</div>
          )}
        </div>
      )}
    </div>
  );
}
